import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import type {ReactElement, ReactNode, SyntheticEvent} from 'react'
import {cloneElement, memo, useCallback, useMemo, useRef} from 'react'

import useUID from '../../../../hooks/useUID'
import {contains} from '../../../../utils/dom'
import {isEventWithSpecialKeyPressed} from '../../../../utils/keys'
import Contents from '../../Contents/Contents'
import ExpandableItemArrow from '../../ExpandableItemArrow/ExpandableItemArrow'
import IconButton from '../../IconButton/IconButton'
import {TreeEventType} from '../../Sidebar/Sidebar.types'
import {keyboardToTreeEvent} from '../../Sidebar/Sidebar.utils'
import {EXPAND_ARROW_WIDTH, TREE_INDENT_WIDTH} from '../Tree.consts'
import type {NodeValue} from '../Tree.types'
import {hasSelection} from '../Tree.utils'

import styles from './TreeNode.css'

export type TreeNodeProps = {
  className: string | null | undefined
  labelClassName?: string | null | undefined
  children: ReactNode | null | undefined
  checked: number
  checkable: boolean
  expanded: boolean
  expandable: boolean | undefined | null
  isLeaf: boolean
  label: string | null | undefined
  level: number
  optimisticToggle: boolean
  treeId: string
  value: NodeValue
  component: ReactElement | null | undefined
  detailsComponent: ReactElement | null | undefined
  focusOnArrow?: boolean
  onCheck?: (value: NodeValue, chacked: boolean) => unknown
  onExpand: (value: NodeValue, expanded: boolean) => unknown
}

function TreeNode(props: TreeNodeProps) {
  const {
    className,
    labelClassName,
    component,
    detailsComponent,
    treeId,
    value,
    label,
    level,
    expanded,
    checked,
    checkable,
    expandable = true,
    optimisticToggle,
    isLeaf,
    focusOnArrow,
    onCheck: onCheckExternal,
    onExpand: onExpandExternal,
  } = props
  const ref = useRef<HTMLDivElement>(null)
  const getCheckState = useCallback(
    (toggle: boolean) => {
      // Toggle off state to checked
      if (checked === 0 && toggle) {
        return true
      }

      // Node is already checked and we are not toggling
      if (checked === 1 && !toggle) {
        return true
      }

      // Get/toggle partial state based on cascade model
      if (checked === 2) {
        return optimisticToggle
      }

      return false
    },
    [checked, optimisticToggle],
  )
  const onCheck = useCallback(
    (e?: SyntheticEvent) => {
      if (e) {
        e.stopPropagation()
      }

      if (onCheckExternal != null) {
        onCheckExternal(value, getCheckState(true))
      }
    },
    [getCheckState, onCheckExternal, value],
  )
  const onClickCheckbox = useCallback((e: SyntheticEvent) => e.stopPropagation(), [])

  const onClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!hasSelection() && !isEventWithSpecialKeyPressed(e) && expandable) {
        onExpandExternal(value, !expanded)
      }
      e.stopPropagation()
    },
    [expanded, onExpandExternal, value, expandable],
  )

  const focusSelfOrArrow = useCallback(
    (el?: HTMLElement | null, options?: FocusOptions) => {
      const focusable = focusOnArrow ? el?.querySelector<HTMLElement>('[data-collapse-arrow]') : el
      focusable?.focus(options)
    },
    [focusOnArrow],
  )

  const onMouseUp = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation()
      if (!hasSelection()) {
        focusSelfOrArrow(ref.current, {
          preventScroll: true,
        })
      }
    },
    [focusSelfOrArrow],
  )

  const getAllNodes = useCallback(
    () => document.querySelectorAll<HTMLElement>(`[data-tree-id='${treeId}']`),
    [treeId],
  )
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation()

      // Block events from popups
      if (!contains(e.currentTarget, e.target)) {
        return
      }

      const item = ref.current
      const triggeredOnItem = e.target === item

      if ([' '].includes(e.key)) {
        if (triggeredOnItem) {
          e.preventDefault()
          onCheck()
        }

        return
      }

      if (['Enter'].includes(e.key)) {
        if (triggeredOnItem) {
          e.preventDefault()
          onClick(e)
        }

        return
      }

      const nodes = [...getAllNodes()]
      const currentIndex = item != null ? nodes.indexOf(item) : -1
      const treeEvent = keyboardToTreeEvent(
        e,
        nodes.map(node => ({
          focusable: true,
          expanded: node.dataset.expanded === 'true',
          level: Number(node.dataset.level),
        })),
        currentIndex,
        0,
        nodes.length - 1,
      )

      switch (treeEvent?.type) {
        case TreeEventType.EVENT_TYPE_FOCUS:
          if (treeEvent.index !== currentIndex || !triggeredOnItem) {
            focusSelfOrArrow(nodes[treeEvent.index])
          }
          break
        case TreeEventType.EVENT_TYPE_EXPAND:
        case TreeEventType.EVENT_TYPE_COLLAPSE:
          onClick(e)
          break
        default:
      }
    },
    [focusSelfOrArrow, getAllNodes, onCheck, onClick],
  )
  const renderCollapseButton = useMemo(() => {
    const b = !isLeaf && expandable
    return b ? (
      <IconButton
        data-collapse-arrow
        tabIndex={focusOnArrow ? 0 : -1}
        className={styles.leftArrow}
        title="Toggle"
        icon={expanded ? 'chevron-down' : 'chevron-right'}
      />
    ) : null
  }, [expandable, expanded, focusOnArrow, isLeaf])
  const id = useUID('tree-node-')

  const renderCheckboxLabel = useCallback(
    (children: ReactNode) => (
      <>
        <div className={styles.checkbox}>
          <Checkbox
            aria-labelledby={id}
            indeterminate={checked === 2}
            checked={checked === 1 || checked === 2}
            onClick={onClickCheckbox}
            onChange={onCheck}
          />
        </div>
        {children}
      </>
    ),
    [checked, id, onCheck, onClickCheckbox],
  )
  const renderLabel = useMemo(() => {
    const labelChild = (
      <Contents id={id}>
        {component != null
          ? cloneElement(component, {
              level,
              expanded,
            })
          : label}
      </Contents>
    )
    return checkable ? renderCheckboxLabel(labelChild) : labelChild
  }, [checkable, component, expanded, id, label, level, renderCheckboxLabel])
  const renderChildren = useMemo(
    () => (
      <Contents
        onClick={e => e.stopPropagation()}
        onMouseUp={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        {detailsComponent != null ? cloneElement(detailsComponent, {level}) : props.children}
      </Contents>
    ),
    [detailsComponent, level, props.children],
  )

  const interactive = expandable && (!isLeaf || detailsComponent != null)
  const nodeClassNames = classNames(className, styles.node, {
    [styles.leaf]: isLeaf,
    [styles.expanded]: expandable && expanded,
  })

  return (
    <div
      className={nodeClassNames}
      role="treeitem"
      aria-labelledby={id}
      aria-expanded={isLeaf ? undefined : expanded}
      aria-selected={checkable ? checked === 1 : undefined}
      onClick={onClick}
      onMouseUp={onMouseUp}
      onKeyDown={onKeyDown}
      tabIndex={focusOnArrow ? -1 : 0}
      data-tree-id={treeId}
      data-expanded={expanded}
      data-level={level}
      ref={ref}
    >
      <div className={classNames(styles.item, {[styles.interactive]: interactive})}>
        <div
          className={classNames(styles.label, labelClassName)}
          style={{
            paddingLeft: `${level * TREE_INDENT_WIDTH + (isLeaf ? EXPAND_ARROW_WIDTH : 0)}px`,
          }}
        >
          {renderCollapseButton}
          {interactive && <div className={styles.background} />}
          {renderLabel}
          {expandable && isLeaf && detailsComponent != null && (
            <ExpandableItemArrow className={styles.rightArrow} expanded={expanded} />
          )}
        </div>
      </div>
      {expanded ? renderChildren : null}
    </div>
  )
}

export default memo(TreeNode)
